<template>
  <v-container fluid class="aml-dash pa-5" style="position:relative">

    <!-- ══ Header ══ -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="aml-overline mb-1">AML Pro &middot; {{ currentMonth }}</div>
        <h2 class="aml-title">Case Management</h2>
      </div>
      <div class="d-flex align-center" style="gap:8px">
        <v-btn depressed small class="aml-btn-ghost" :loading="loading" @click="loadStats">
          <v-icon small left>mdi-refresh</v-icon>Refresh
        </v-btn>
        <v-btn
          depressed small class="aml-btn-analyse"
          @click="runAnalysis"
        >
          <v-icon small left>mdi-magnify-scan</v-icon>Run analysis
        </v-btn>
        <v-btn depressed small class="aml-btn-primary" @click="openNewCaseDialog">
          <v-icon small left>mdi-plus</v-icon>New case
        </v-btn>
      </div>
    </div>

    <!-- ══ View tabs ══ -->
    <div class="aml-view-tabs mb-4">
      <button
        class="aml-view-tab"
        :class="{ 'aml-view-tab--active': dashView === 'cases' }"
        @click="dashView = 'cases'"
      >
        <v-icon x-small left>mdi-folder-open-outline</v-icon>Cases
      </button>
      <button
        class="aml-view-tab"
        :class="{ 'aml-view-tab--active': dashView === 'alerts' }"
        @click="dashView = 'alerts'"
      >
        <v-icon x-small left>mdi-bell-alert-outline</v-icon>Alerts
        <span v-if="openAlertCount > 0" class="aml-view-tab-badge">{{ openAlertCount }}</span>
      </button>
    </div>

    <!-- ══ Cases view ══ -->
    <div v-show="dashView === 'cases'">
    <v-row dense class="mb-5">
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-blue': tableFilter === 'active' }"
          @click="setTableFilter('active')"
        >
          <div class="aml-metric-label">Active cases</div>
          <div class="aml-metric-value" style="color:#185FA5">{{ stats.metrics && stats.metrics.open }}</div>
          <div class="aml-metric-sub">All open cases</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-red': tableFilter === 'attention' }"
          @click="setTableFilter('attention')"
        >
          <div class="aml-metric-label">High priority</div>
          <div class="aml-metric-value" style="color:#E24B4A">{{ stats.metrics && stats.metrics.highPriority }}</div>
          <div class="aml-metric-sub">Needs immediate attention</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-green': tableFilter === 'closed' }"
          @click="setTableFilter('closed')"
        >
          <div class="aml-metric-label">Closed this month</div>
          <div class="aml-metric-value" style="color:#3B6D11">{{ stats.metrics && stats.metrics.closedThisMonth }}</div>
          <div class="aml-metric-sub">Filed &middot; no action &middot; completed</div>
        </div>
      </v-col>
      <v-col cols="6" sm="3">
        <div
          class="aml-metric-card"
          :class="{ 'aml-metric-active aml-metric-purple': tableFilter === 'mine' }"
          @click="setTableFilter('mine')"
        >
          <div class="aml-metric-label">Assigned to me</div>
          <div class="aml-metric-value" style="color:#534AB7">{{ stats.metrics && stats.metrics.assignedToMe }}</div>
          <div class="aml-metric-sub">Your open cases</div>
        </div>
      </v-col>
    </v-row>

    <!-- ══ Main grid ══ -->
    <v-row dense>

      <!-- ── Left: cases table ── -->
      <v-col cols="12" md="8">
        <div class="aml-card">

          <!-- Toolbar -->
          <div class="aml-toolbar">
            <!-- Filter tabs -->
            <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
              <button
                v-for="tab in filterTabs"
                :key="tab.key"
                class="aml-filter-tab"
                :class="tableFilter === tab.key ? 'aml-filter-tab--active' : ''"
                :style="tableFilter === tab.key ? `border-color:${tab.color};background:${tab.color}18;color:${tab.color}` : ''"
                @click="setTableFilter(tab.key)"
              >
                {{ tab.label }}
                <span
                  class="aml-filter-count"
                  :style="tableFilter === tab.key ? `background:${tab.color}28;color:${tab.color}` : ''"
                >{{ tab.count }}</span>
              </button>

              <v-chip
                v-if="hasActiveFilters"
                x-small close outlined color="primary"
                class="ml-1"
                @click:close="clearFilters"
              >
                <v-icon x-small left>mdi-filter</v-icon>Filters active
              </v-chip>
            </div>

            <div class="d-flex align-center" style="gap:8px">
              <!-- Inline search -->
              <div class="aml-search-wrap">
                <v-icon x-small class="aml-search-icon">mdi-magnify</v-icon>
                <input
                  v-model="tableSearch"
                  class="aml-search-input"
                  placeholder="Search cases..."
                />
              </div>
              <v-btn depressed x-small class="aml-btn-ghost" @click="caseFilterDialog = true">
                <v-icon x-small left>mdi-filter-outline</v-icon>More filters
              </v-btn>
            </div>
          </div>

          <!-- Table -->
          <div class="aml-table-wrap">
            <table class="aml-table">
              <colgroup>
                <col style="width:15%">
                <col style="width:13%">
                <col style="width:8%">
                <col style="width:15%">
                <col style="width:10%">
                <col style="width:17%">
                <col style="width:12%">
                <col style="width:10%">
              </colgroup>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Stage</th>
                  <th>Priority</th>
                  <th>Assigned to</th>
                  <th>Opened</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredCasesForTable.length">
                  <td colspan="8" class="aml-empty-row">No cases match this filter</td>
                </tr>
                <tr
                  v-for="item in filteredCasesForTable"
                  :key="item.id"
                  class="aml-table-row"
                  @click="goToCase(item.id)"
                >
                  <td>
                    <span class="aml-ref-link">{{ item.caseReference }}</span>
                  </td>
                  <td class="aml-muted">{{ item.account && item.account.mainAccountNumber }}</td>
                  <td>
                    <span class="aml-type-badge">{{ item.reportType }}</span>
                  </td>
                  <td>
                    <span
                      class="aml-pill"
                      :style="stageStyle(item)"
                    >{{ stageLabel(item) }}</span>
                  </td>
                  <td>
                    <span
                      class="aml-pill"
                      :style="priorityStyle(item.priority)"
                    >{{ item.priority }}</span>
                  </td>
                  <td @click.stop>
                    <div v-if="assigneeName(item.assignedTo) !== '—'" class="d-flex align-center" style="gap:6px">
                      <div class="aml-avatar" :style="avatarStyle(assigneeName(item.assignedTo))">
                        {{ avatarInitials(assigneeName(item.assignedTo)) }}
                      </div>
                      <span class="aml-muted aml-truncate">{{ assigneeName(item.assignedTo) }}</span>
                    </div>
                    <span v-else class="aml-unassigned">Unassigned</span>
                  </td>
                  <td class="aml-muted">{{ fmtDate(item.dateCreated) }}</td>
                  <td @click.stop>
                    <div class="d-flex align-center justify-end" style="gap:4px">
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <button class="aml-icon-btn" v-on="on" @click="goToCase(item.id)">
                            <v-icon small color="#185FA5">mdi-eye</v-icon>
                          </button>
                        </template>
                        <span>View case</span>
                      </v-tooltip>

                      <template v-if="!isClosedStatus(item.status)">
                        <v-tooltip bottom>
                          <template #activator="{ on }">
                            <button class="aml-icon-btn" v-on="on" @click="openAssignDialog(item)">
                              <v-icon small color="#534AB7">mdi-account-arrow-right-outline</v-icon>
                            </button>
                          </template>
                          <span>{{ item.assignedToUserId ? 'Reassign' : 'Assign' }}</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                          <template #activator="{ on }">
                            <button class="aml-icon-btn" v-on="on" :disabled="!item.assignedToUserId" @click="openDeassignDialog(item)">
                              <v-icon small :color="item.assignedToUserId ? '#D85A30' : '#ccc'">mdi-account-remove-outline</v-icon>
                            </button>
                          </template>
                          <span>Deassign</span>
                        </v-tooltip>
                      </template>

                      <template v-else>
                        <v-tooltip bottom>
                          <template #activator="{ on }">
                            <button class="aml-icon-btn" v-on="on" @click="openReopenDialog(item)">
                              <v-icon small color="#3B6D11">mdi-lock-open-outline</v-icon>
                            </button>
                          </template>
                          <span>Reopen case</span>
                        </v-tooltip>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="aml-table-footer">
            <span>{{ tableTotalText }}</span>
            <div class="d-flex align-center" style="gap:6px" v-if="tableTotal > tableLimit">
              <button
                class="aml-page-btn"
                :disabled="tablePage <= 1"
                @click="changePage(tablePage - 1)"
              >
                <v-icon small>mdi-chevron-left</v-icon>
              </button>
              <span style="font-size:11px;color:#777">
                {{ tablePage }} / {{ tableTotalPages }}
              </span>
              <button
                class="aml-page-btn"
                :disabled="tablePage >= tableTotalPages"
                @click="changePage(tablePage + 1)"
              >
                <v-icon small>mdi-chevron-right</v-icon>
              </button>
            </div>
          </div>
        </div>
      </v-col>

      <!-- ── Right panel ── -->
      <v-col cols="12" md="4">

        <!-- My open cases -->
        <div class="aml-card mb-3">
          <div class="aml-panel-header">
            <span>My open cases</span>
            <span class="aml-count-pill aml-count-purple">{{ stats.assignedToMe ? stats.assignedToMe.length : 0 }}</span>
          </div>
          <div v-if="!stats.assignedToMe || !stats.assignedToMe.length" class="aml-empty-panel">
            No open cases assigned to you.
          </div>
          <div
            v-for="c in stats.assignedToMe"
            :key="c.id"
            class="aml-my-case-row"
            @click="goToCase(c.id)"
          >
            <div>
              <div class="aml-ref-link" style="font-size:12px">{{ c.caseReference }}</div>
              <div class="aml-muted" style="font-size:11px;margin-top:2px">{{ c.reportStage || 'Open' }}</div>
            </div>
            <v-icon x-small color="#ccc">mdi-chevron-right</v-icon>
          </div>
        </div>

        <!-- Pipeline -->
        <div class="aml-card mb-3">
          <div class="aml-panel-header">
            <span>Investigation pipeline</span>
          </div>
          <div class="pa-3">
            <div v-if="!stats.pipeline || !stats.pipeline.length" class="aml-empty-panel">
              No active cases.
            </div>
            <div v-for="(item, i) in stats.pipeline" :key="item.label" class="mb-3">
              <div class="d-flex justify-space-between mb-1">
                <span style="font-size:12px;color:var(--color-text-secondary, #666)">{{ item.label }}</span>
                <span style="font-size:12px;font-weight:600">{{ item.count }}</span>
              </div>
              <div class="aml-progress-track">
                <div
                  class="aml-progress-bar"
                  :style="`width:${pipelinePct(item.count)}%;background:${pipelineColors[i % pipelineColors.length]}`"
                />
              </div>
            </div>

            <template v-if="stats.closedBreakdown && stats.closedBreakdown.some(b => b.count > 0)">
              <div class="aml-divider my-2" />
              <div class="aml-overline mb-2">Closed this month</div>
              <div
                v-for="item in stats.closedBreakdown"
                :key="'c-' + item.label"
                v-if="item.count > 0"
                class="d-flex justify-space-between mb-1"
              >
                <span style="font-size:11px;color:var(--color-text-secondary, #666)">{{ item.label }}</span>
                <span style="font-size:11px;font-weight:600">{{ item.count }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- Recent activity -->
        <AmlActivityFeed title="Recent activity" :compact="true" @go-to-case="goToCase" />

      </v-col>
    </v-row>

    </div>

    <!-- ══ Alerts view ══ -->
    <div v-show="dashView === 'alerts'">
      <AmlAlertManager
        ref="alertManager"
        @go-to-case="goToCase"
        @case-created="onCaseCreated"
        @analysis-complete="onAnalysisComplete"
      />
    </div>

    <!-- New Case Dialog -->
    <v-dialog v-model="newCaseDialog" max-width="680" persistent scrollable>
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-center">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-folder-plus-outline</v-icon>
            <span class="text-h6 font-weight-medium">Open new AML case</span>
          </div>
          <v-btn icon @click="closeNewCase"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="min-height:500px">
          <div class="caption grey--text text-uppercase font-weight-medium mt-3 mb-2" style="letter-spacing:.5px">Source</div>
          <v-btn-toggle v-model="newCase.source" mandatory dense class="mb-4">
            <v-btn value="MANUAL" small depressed>Manual</v-btn>
            <v-btn value="ALERT" small depressed>From alert</v-btn>
            <v-btn value="ATTEMPT" small depressed>From flagged attempt</v-btn>
          </v-btn-toggle>

          <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Account</div>
          <v-card v-if="newCase.selectedAccount" outlined class="mb-3 pa-3" color="blue lighten-5">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="font-weight-medium">{{ newCase.selectedAccount.personName }}</div>
                <div class="caption primary--text">{{ newCase.selectedAccount.mainAccountNumber }}<span v-if="newCase.selectedAccount.trn"> · TRN: {{ newCase.selectedAccount.trn }}</span></div>
              </div>
              <v-btn icon small @click="clearSelectedAccount"><v-icon small>mdi-close-circle</v-icon></v-btn>
            </div>
          </v-card>

          <div v-else>
            <v-row dense class="mb-2">
              <v-col cols="9">
                <v-text-field v-model="accountSearch.query" label="Search by name or TRN" dense outlined hide-details @keyup.enter="searchAccounts" />
              </v-col>
              <v-col cols="3">
                <v-btn depressed color="primary" small :loading="searchingAccounts" @click="searchAccounts" style="height:40px;width:100%">
                  <v-icon small>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="accountSearch.results.length">
              <v-list dense outlined style="border-radius:8px;overflow:hidden">
                <v-list-item v-for="r in accountSearch.results" :key="r.accountId" dense style="cursor:pointer" @click="selectAccount(r)">
                  <v-list-item-icon class="mr-2"><v-icon small color="primary">mdi-account-circle-outline</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="caption font-weight-medium">{{ r.personName }}</v-list-item-title>
                    <v-list-item-subtitle class="caption">{{ r.mainAccountNumber }}<span v-if="r.trn"> · TRN: {{ r.trn }}</span></v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action><v-btn x-small depressed color="primary">Select</v-btn></v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
            <div v-if="accountSearch.searched && !accountSearch.results.length" class="caption grey--text text-center py-2">No results. Try a different name or TRN.</div>
          </div>

          <v-divider class="my-3" />

          <!-- Alert picker -->
          <div v-if="newCase.source === 'ALERT' && newCase.selectedAccount">
            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Select alert</div>
            <div v-if="loadingAlerts" class="text-center py-2"><v-progress-circular indeterminate size="20" color="primary" /></div>
            <div v-else-if="!openAlerts.length" class="caption grey--text text-center py-2">No open unlinked alerts for this account.</div>
            <v-card v-for="a in openAlerts" :key="a.id" outlined class="mb-2 pa-2" :style="newCase.amlAlertId === a.id ? 'border-color:#1565C0;background:#E3F2FD' : ''" style="cursor:pointer" @click="newCase.amlAlertId = a.id">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption font-weight-medium">{{ a.rule && a.rule.ruleName }}</div>
                  <div class="caption grey--text">{{ a.triggerAmount ? `$${Number(a.triggerAmount).toLocaleString()}` : '' }} · {{ a.mainAccountNumber }} · {{ fmtDate(a.dateCreated) }}</div>
                </div>
                <div class="d-flex align-center gap-1">
                  <v-chip x-small :color="riskChipColor(a.riskScore)" dark>Risk: {{ a.riskScore }}</v-chip>
                  <v-icon v-if="newCase.amlAlertId === a.id" small color="primary">mdi-check-circle</v-icon>
                </div>
              </div>
            </v-card>
            <v-divider class="my-3" />
          </div>

          <!-- Flagged attempt picker -->
          <div v-if="newCase.source === 'ATTEMPT' && newCase.selectedAccount">
            <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Select flagged attempt</div>
            <div v-if="loadingAttempts" class="text-center py-2"><v-progress-circular indeterminate size="20" color="primary" /></div>
            <div v-else-if="!flaggedAttempts.length" class="caption grey--text text-center py-2">No unlinked flagged attempts for this account.</div>
            <v-card v-for="a in flaggedAttempts" :key="a.id" outlined class="mb-2 pa-2" :style="newCase.linkedAttemptId === a.id ? 'border-color:#1565C0;background:#E3F2FD' : ''" style="cursor:pointer" @click="newCase.linkedAttemptId = a.id">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="caption font-weight-medium">{{ a.suspicionReason }}</div>
                  <div class="caption grey--text">{{ a.amount ? `${a.currency} ${Number(a.amount).toLocaleString()}` : '' }} · {{ a.transactionDate }} · Logged by: {{ assigneeName(a.flaggedBy) }}</div>
                </div>
                <v-icon v-if="newCase.linkedAttemptId === a.id" small color="primary">mdi-check-circle</v-icon>
              </div>
            </v-card>
            <v-divider class="my-3" />
          </div>

          <!-- Case fields -->
          <div class="caption grey--text text-uppercase font-weight-medium mb-2" style="letter-spacing:.5px">Case details</div>
          <v-form ref="newCaseForm">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-select v-model="newCase.reportType" :items="reportTypeOptions" label="Report Type *" dense outlined :rules="[v => !!v || 'Required']" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select v-model="newCase.priority" :items="priorityOptions" label="Priority" dense outlined />
              </v-col>
              <v-col cols="12">
                <v-autocomplete v-model="newCase.assignedToUserId" :items="users" :loading="loadingUsers" item-text="displayName" item-value="id" label="Assign To *" dense outlined no-data-text="No users found" :rules="[v => !!v || 'Required']" />
              </v-col>
              <v-col cols="12">
                <v-textarea v-model="newCase.summary" label="Summary *" rows="3" dense outlined :rules="[v => !!v || 'Required']" hint="Describe the suspicious activity" persistent-hint />
              </v-col>
              <v-col cols="12" v-if="newCase.source === 'MANUAL'">
                <v-autocomplete v-model="newCase.cycleId" :items="cycles" :loading="loadingCycles" item-text="cycleName" item-value="id" label="Monitoring cycle (optional)" dense outlined clearable hint="If not selected, the system will auto-match based on the alert rule type" persistent-hint no-data-text="No active cycles found">
                  <template #item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.cycleName }}</v-list-item-title>
                      <v-list-item-subtitle class="caption">{{ item.ruleTypes.join(', ') }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeNewCase">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="saving" :disabled="!newCase.selectedAccount" @click="submitNewCase">Open case</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Dialog -->
    <v-dialog v-model="assignDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          Assign case
          <v-spacer />
          <v-btn icon @click="assignDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="caption grey--text mb-1">Case: <strong>{{ assignTarget && assignTarget.caseReference }}</strong></div>
          <v-chip
            small
            outlined
            :color="assignToUserId === (loggedInUser && loggedInUser.id) ? 'primary' : undefined"
            class="mb-3"
            @click="assignToUserId = loggedInUser && loggedInUser.id"
          >
            <v-icon small left>mdi-account-check-outline</v-icon>Assign to me
          </v-chip>
          <v-autocomplete v-model="assignToUserId" :items="users" :loading="loadingUsers" item-text="displayName" item-value="id" label="Assign to *" dense outlined no-data-text="No users found" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="assignDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="assigning" :disabled="!assignToUserId" @click="submitAssign">Assign</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Open Case Prompt -->
    <v-dialog v-model="openCasePromptDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-folder-open-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Open this case?</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2">
          <strong>{{ openCasePromptTarget && openCasePromptTarget.caseReference }}</strong> has been assigned to you. Would you like to open it now?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="openCasePromptDialog = false">Not now</v-btn>
          <v-btn depressed color="primary" @click="goToCaseFromPrompt">
            <v-icon left small>mdi-arrow-right</v-icon>Open case
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reopen Case Dialog -->
    <v-dialog v-model="reopenDialog" max-width="480" persistent>
      <v-card>
        <template v-if="reopenStep === 1">
          <v-card-title class="d-flex align-center">
            <v-icon left color="green darken-1">mdi-lock-open-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">Reopen case?</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4 body-2">
            <strong>{{ reopenTarget && reopenTarget.caseReference }}</strong> is currently closed as <em>{{ reopenTarget && formatStatus(reopenTarget.status) }}</em>.<br><br>
            Reopening will set the status back to <strong>Open</strong>.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="reopenDialog = false">Cancel</v-btn>
            <v-btn depressed color="green darken-1" dark :loading="reopening" @click="confirmReopen">
              <v-icon left small>mdi-lock-open-outline</v-icon>Reopen
            </v-btn>
          </v-card-actions>
        </template>

        <template v-else-if="reopenStep === 2">
          <v-card-title class="d-flex align-center">
            <v-icon left color="green darken-1">mdi-check-circle-outline</v-icon>
            <span class="subtitle-1 font-weight-medium">Case reopened</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <div class="body-2 mb-4"><strong>{{ reopenTarget && reopenTarget.caseReference }}</strong> is now open. What would you like to do next?</div>
            <v-list dense>
              <v-list-item style="cursor:pointer;border:1px solid #e0e0e0;border-radius:8px;margin-bottom:8px" @click="reopenAssignToSelf">
                <v-list-item-icon class="mr-3"><v-avatar size="36" color="blue lighten-4"><v-icon color="blue darken-1">mdi-account-check-outline</v-icon></v-avatar></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-medium">Assign to me</v-list-item-title>
                  <v-list-item-subtitle class="caption">Take ownership and start working</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action><v-icon color="grey lighten-1">mdi-chevron-right</v-icon></v-list-item-action>
              </v-list-item>
              <v-list-item style="cursor:pointer;border:1px solid #e0e0e0;border-radius:8px;margin-bottom:8px" @click="reopenStep = 3">
                <v-list-item-icon class="mr-3"><v-avatar size="36" color="teal lighten-4"><v-icon color="teal darken-1">mdi-account-arrow-right-outline</v-icon></v-avatar></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-medium">Assign to another officer</v-list-item-title>
                  <v-list-item-subtitle class="caption">Select a team member</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action><v-icon color="grey lighten-1">mdi-chevron-right</v-icon></v-list-item-action>
              </v-list-item>
              <v-list-item style="cursor:pointer;border:1px solid #e0e0e0;border-radius:8px" @click="closeReopenDialog">
                <v-list-item-icon class="mr-3"><v-avatar size="36" color="grey lighten-3"><v-icon color="grey darken-1">mdi-format-list-bulleted</v-icon></v-avatar></v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="body-2 font-weight-medium">Return to active cases</v-list-item-title>
                  <v-list-item-subtitle class="caption">Leave unassigned for now</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action><v-icon color="grey lighten-1">mdi-chevron-right</v-icon></v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </template>

        <template v-else-if="reopenStep === 3">
          <v-card-title class="d-flex align-center">
            <v-btn icon small @click="reopenStep = 2" class="mr-1"><v-icon small>mdi-arrow-left</v-icon></v-btn>
            <span class="subtitle-1 font-weight-medium">Assign to officer</span>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-autocomplete v-model="reopenAssignUserId" :items="users" :item-text="u => assigneeName(u)" item-value="id" label="Select officer *" dense outlined prepend-inner-icon="mdi-account-search-outline" :loading="loadingUsers" no-data-text="No users found" />
          </v-card-text>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="reopenStep = 2">Back</v-btn>
            <v-btn depressed color="teal" dark :disabled="!reopenAssignUserId" :loading="assigning" @click="confirmReopenAssign">
              <v-icon left small>mdi-account-arrow-right-outline</v-icon>Assign
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <!-- Deassign Dialog -->
    <v-dialog v-model="deassignDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange darken-2">mdi-account-remove-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Deassign case?</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2">
          This will remove the current officer assignment from <strong>{{ deassignTarget && deassignTarget.caseReference }}</strong>. The case will remain open but unassigned.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deassignDialog = false">Cancel</v-btn>
          <v-btn depressed color="orange darken-2" dark :loading="deassigning" @click="confirmDeassign">Deassign</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Case Filter Dialog -->
    <v-dialog v-model="caseFilterDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon left color="primary">mdi-filter-outline</v-icon>
            <span class="subtitle-1">Filter cases</span>
          </div>
          <v-btn icon @click="caseFilterDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.status" :items="statusFilterOptions" item-text="text" item-value="value" label="Case status" clearable dense outlined prepend-inner-icon="mdi-list-status" hint="Filter by open or closed outcome" persistent-hint />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.priority" :items="priorityOptions" label="Priority" clearable dense outlined prepend-inner-icon="mdi-flag-outline" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-select v-model="filters.reportType" :items="reportTypeOptions" label="Report type" clearable dense outlined prepend-inner-icon="mdi-file-document-outline" />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model="filters.mainAccountNumber" label="Account number" clearable dense outlined prepend-inner-icon="mdi-card-account-details-outline" />
            </v-col>
            <v-col cols="12">
              <v-select v-model="filters.reportStage" :items="stageNameOptions" label="Investigation stage" clearable dense outlined prepend-inner-icon="mdi-progress-clock" hint="Filter by the case's current monitoring cycle stage" persistent-hint :loading="loadingStages" no-data-text="No stages found" />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-btn text color="grey darken-1" :disabled="!hasActiveFilters" @click="clearFiltersAndClose">
            <v-icon left small>mdi-close</v-icon>Clear all
          </v-btn>
          <v-spacer />
          <v-btn text @click="caseFilterDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="tableLoading" @click="applyFiltersAndClose">
            <v-icon left small>mdi-magnify</v-icon>Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import PageMixin    from '@/mixins/page.mixin';
import FormatMixin  from '@/mixins/format.mixin';
import AmlMixin     from '@/mixins/aml.mixin';
import { AmlCaseService } from '@/modules/amlpro/services/aml-case.service';
import { MemberProfileService } from '@/modules/amlpro/services/member-profile-service';
import { UserService } from '@/modules/global-settings/security/services/user.service';
import { TransactionMonitoringCycleService } from '@/modules/global-settings/aml/services/transaction-monitoring-cycle.service';
import AmlActivityFeed   from './AmlActivityFeed.vue';
import AmlAlertManager  from './AmlAlertManager.vue';

const svc       = new AmlCaseService();
const memberSvc = new MemberProfileService();
const userSvc   = new UserService();
const cycleSvc  = new TransactionMonitoringCycleService();

const EMPTY_STATS = {
  metrics: { open:0, closedThisMonth:0, highPriority:0, active:0, assignedToMe:0, closedInvestigation:0, closedNoAction:0, closedReportFiled:0, totalClosed:0 },
  pipeline: [], closedBreakdown: [], assignedToMe: [], recentCases: [], recentActivity: [],
};

const FRESH_CASE = () => ({
  source: 'MANUAL', selectedAccount: null, amlAlertId: null,
  linkedAttemptId: null, cycleId: null, reportType: '',
  priority: 'MEDIUM', assignedToUserId: '', summary: '',
});

export default {
  name: 'AmlCaseDashboard',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  components: { AmlActivityFeed, AmlAlertManager },

  data() {
    return {
      loading: false, tableLoading: false, saving: false, assigning: false,
      deassigning: false, loadingUsers: false, loadingAlerts: false,
      loadingAttempts: false, loadingCycles: false, searchingAccounts: false, reopening: false,
      runningAnalysis: false,

      dashView: 'cases',
      openAlertCount: 0,

      stats: { ...EMPTY_STATS },
      tableFilter: 'active',
      tableSearch: '',
      filters: { status: null, priority: null, reportType: null, mainAccountNumber: '', reportStage: '' },
      filteredCases: null,
      tablePage:  1,
      tableLimit: 20,
      tableTotal: 0,

      newCaseDialog: false, newCase: FRESH_CASE(),
      cycles: [], openAlerts: [], flaggedAttempts: [], users: [],
      accountSearch: { query: '', results: [], searched: false },

      assignDialog: false, assignTarget: null, assignToUserId: '',
      deassignDialog: false, deassignTarget: null,
      openCasePromptDialog: false, openCasePromptTarget: null,
      reopenDialog: false, reopenTarget: null, reopening: false,
      reopenStep: 1, reopenAssignUserId: null,
      caseFilterDialog: false, stageNameOptions: [], loadingStages: false,

      statusOptions: ['OPEN','CLOSED_INVESTIGATION_COMPLETED','CLOSED_NO_ACTION','CLOSED_REPORT_FILED'],
      statusFilterOptions: [
        { text: 'Open',                   value: 'OPEN' },
        { text: 'Closed — Investigation', value: 'CLOSED_INVESTIGATION_COMPLETED' },
        { text: 'Closed — No action',     value: 'CLOSED_NO_ACTION' },
        { text: 'Closed — Report filed',  value: 'CLOSED_REPORT_FILED' },
      ],
      priorityOptions:   ['HIGH','MEDIUM','LOW'],
      reportTypeOptions: ['STR','CTR','SAR','OTHER'],

      pipelineColors: ['#378ADD','#1D9E75','#534AB7','#D85A30','#BA7517','#185FA5'],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    filterTabs() {
      return [
        { key: 'active',    label: 'Open',          count: this.stats.metrics?.open ?? 0,           color: '#378ADD' },
        { key: 'attention', label: 'High priority',  count: this.stats.metrics?.highPriority ?? 0,   color: '#E24B4A' },
        { key: 'closed',    label: 'Closed',         count: this.stats.metrics?.closedThisMonth ?? 0, color: '#3B6D11' },
        { key: 'mine',      label: 'Mine',           count: this.stats.metrics?.assignedToMe ?? 0,   color: '#534AB7' },
      ];
    },

    hasActiveFilters() {
      return !!(this.filters.status || this.filters.priority || this.filters.reportType || this.filters.mainAccountNumber || this.filters.reportStage);
    },

    currentMonth() {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },

    pipelineMax() {
      return Math.max(...(this.stats.pipeline.map(p => p.count)), 1);
    },

    displayedCases() {
      // Always use paginated API results — filteredCases is populated by loadPagedCases()
      return this.filteredCases ?? [];
    },

    filteredCasesForTable() {
      if (!this.tableSearch.trim()) return this.displayedCases;
      const q = this.tableSearch.toLowerCase();
      return this.displayedCases.filter(c =>
        c.caseReference?.toLowerCase().includes(q) ||
        c.account?.mainAccountNumber?.includes(q),
      );
    },

    tableTotalPages() {
      return Math.max(1, Math.ceil(this.tableTotal / this.tableLimit));
    },

    tableTotalText() {
      const showing = this.filteredCasesForTable.length;
      if (this.tableTotal > this.tableLimit) {
        const from = (this.tablePage - 1) * this.tableLimit + 1;
        const to   = Math.min(this.tablePage * this.tableLimit, this.tableTotal);
        return `Showing ${from}–${to} of ${this.tableTotal} cases`;
      }
      return `${showing} case${showing !== 1 ? 's' : ''}`;
    },
  },

  watch: {
    'newCase.source'(val) {
      if (!this.newCase.selectedAccount) return;
      if (val === 'ALERT')   this.loadOpenAlerts();
      if (val === 'ATTEMPT') this.loadFlaggedAttempts();
    },
  },

  mounted() {
    this.loadStats();
    this.loadStageNames();
    this.loadOpenAlertCount();
  },

  methods: {
    onCaseCreated(newCase) {
      this.loadStats();
      if (newCase) {
        this.dashView = 'cases';
        this.showSnack({
          color:   'success',
          title:   'Case opened',
          message: `${newCase.caseReference} is now visible in the Cases tab`,
        });
      }
    },

    onAnalysisComplete() {
      this.dashView = 'alerts';
      this.loadOpenAlertCount();
    },

    async loadOpenAlertCount() {
      try {
        const res  = await svc.fetchAlerts({ limit: 1, page: 1, status: 'OPEN' });
        const data = res.data ?? res;
        this.openAlertCount = data.total ?? 0;
      } catch { /* silent */ }
    },

    stageLabel(item) {
      if (this.isClosedStatus(item.status)) {
        return this.formatStatus(item.status);
      }
      return item.reportStage || 'Open';
    },

    stageStyle(item) {
      const map = {
        OPEN:                           'background:#E6F1FB;color:#185FA5',
        CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11',
        CLOSED_NO_ACTION:               'background:#F1EFE8;color:#5F5E5A',
        CLOSED_REPORT_FILED:            'background:#EEEDFE;color:#534AB7',
      };
      return map[item.status] || 'background:#E6F1FB;color:#185FA5';
    },

    priorityStyle(p) {
      const map = {
        HIGH:   'background:#FCEBEB;color:#A32D2D',
        MEDIUM: 'background:#FAEEDA;color:#854F0B',
        LOW:    'background:#EAF3DE;color:#3B6D11',
      };
      return map[p] || 'background:#F1EFE8;color:#5F5E5A';
    },

    avatarInitials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },

    avatarStyle(name) {
      const palettes = [
        'background:#B5D4F4;color:#185FA5',
        'background:#9FE1CB;color:#0F6E56',
        'background:#CECBF6;color:#3C3489',
        'background:#F5C4B3;color:#993C1D',
        'background:#FAC775;color:#854F0B',
      ];
      const idx = name ? name.charCodeAt(0) % palettes.length : 0;
      return palettes[idx];
    },

    async loadStageNames() {
      this.loadingStages = true;
      try {
        const res    = await cycleSvc.fetchAll();
        const cycles = res.data ?? res;
        const names  = new Set();
        for (const cycle of (Array.isArray(cycles) ? cycles : (cycles.data ?? []))) {
          for (const stage of (cycle.stages ?? [])) {
            if (stage.stageName) names.add(stage.stageName);
          }
        }
        this.stageNameOptions = [...names].sort();
      } catch { /* silent */ } finally {
        this.loadingStages = false;
      }
    },

    async runAnalysis() {
      this.dashView = 'alerts';
      await this.$nextTick();
      this.$refs.alertManager?.runAnalysis();
    },

    async loadStats() {
      this.loading = true;
      try {
        this.stats = await svc.fetchDashboardStats().then(r => r.data ?? r);
        await this.loadPagedCases();
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load dashboard' });
      } finally {
        this.loading = false;
      }
    },

    setTableFilter(f) {
      this.tableFilter   = f;
      this.filteredCases = null;
      this.tableSearch   = '';
      this.tablePage     = 1;
      this.loadPagedCases();
    },

    async changePage(page) {
      this.tablePage = page;
      await this.loadPagedCases();
    },

    async loadPagedCases() {
      this.tableLoading = true;
      try {
        const filterMap = {
          active:    { status: 'OPEN' },
          attention: { status: 'OPEN', priority: 'HIGH' },
          closed:    { status: 'CLOSED_INVESTIGATION_COMPLETED,CLOSED_NO_ACTION,CLOSED_REPORT_FILED' },
          mine:      { status: 'OPEN', assignedToUserId: this.loggedInUser?.id },
        };
        const base   = filterMap[this.tableFilter] ?? { status: 'OPEN' };
        const params = { page: this.tablePage, limit: this.tableLimit, ...base, ...this.activeFilters() };
        const res    = await svc.fetchAll(params);
        const data   = res.data ?? res;
        this.filteredCases = data.data ?? data;
        this.tableTotal    = data.total ?? (data.data ?? data).length;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load cases' });
      } finally {
        this.tableLoading = false;
      }
    },

    async loadFilteredCases() {
      const hasFilter = this.filters.status || this.filters.priority || this.filters.reportType || this.filters.mainAccountNumber;
      if (!hasFilter) { this.filteredCases = null; return; }
      this.tableLoading = true;
      try {
        const params       = { page: 1, limit: 50, ...this.activeFilters() };
        const res          = await svc.fetchAll(params);
        const data         = res.data ?? res;
        this.filteredCases = data.data ?? data;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to filter cases' });
      } finally {
        this.tableLoading = false;
      }
    },

    activeFilters() {
      const f = {};
      if (this.filters.status)            f.status            = this.filters.status;
      if (this.filters.priority)          f.priority          = this.filters.priority;
      if (this.filters.reportType)        f.reportType        = this.filters.reportType;
      if (this.filters.mainAccountNumber) f.mainAccountNumber = this.filters.mainAccountNumber;
      if (this.filters.reportStage)       f.reportStage       = this.filters.reportStage;
      return f;
    },

    clearFilters() {
      this.filters       = { status: null, priority: null, reportType: null, mainAccountNumber: '', reportStage: '' };
      this.filteredCases = null;
      this.tableFilter   = 'active';
      this.tableSearch   = '';
      this.tablePage     = 1;
      this.loadPagedCases();
    },

    clearFiltersAndClose() { this.clearFilters(); this.caseFilterDialog = false; },
    applyFiltersAndClose()  { this.caseFilterDialog = false; this.tablePage = 1; this.loadPagedCases(); },

    async openAssignDialog(item) {
      this.assignTarget   = item;
      this.assignToUserId = item.assignedToUserId ?? '';
      this.assignDialog   = true;
      if (!this.users.length)  await this.loadUsers();
      if (!this.cycles.length) await this.loadCycles();
    },

    async submitAssign() {
      this.assigning = true;
      try {
        const target = this.assignTarget;
        await svc.assign(target.id, this.assignToUserId);
        this.assignDialog = false;
        this.loadStats();
        if (this.assignToUserId === (this.loggedInUser && this.loggedInUser.id)) {
          this.openCasePromptTarget = target;
          this.openCasePromptDialog = true;
        } else {
          this.showSnack({ color: 'success', title: 'Assigned', message: `Case ${target.caseReference} reassigned` });
        }
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: e?.response?.data?.message ?? 'Failed to assign' });
      } finally {
        this.assigning = false;
      }
    },

    openDeassignDialog(item) { this.deassignTarget = item; this.deassignDialog = true; },

    async confirmDeassign() {
      this.deassigning = true;
      try {
        await svc.deassign(this.deassignTarget.id);
        this.showSnack({ color: 'success', title: 'Deassigned', message: `Case ${this.deassignTarget.caseReference} deassigned` });
        this.deassignDialog = false;
        this.deassignTarget = null;
        this.loadStats();
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to deassign' });
      } finally {
        this.deassigning = false;
      }
    },

    async openNewCaseDialog() {
      this.newCase       = FRESH_CASE();
      this.accountSearch = { query: '', results: [], searched: false };
      this.openAlerts    = [];
      this.flaggedAttempts = [];
      this.newCaseDialog = true;
      if (!this.users.length)  await this.loadUsers();
      if (!this.cycles.length) await this.loadCycles();
    },

    closeNewCase() {
      this.newCaseDialog = false;
      this.$refs.newCaseForm?.reset();
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const res  = await userSvc.findAll();
        const data = res.data ?? res;
        const list = data.users ?? data;
        this.users = list.map(u => ({
          id: u.id,
          displayName: u.person
            ? this.capitalizeName(`${u.person.firstName ?? ''} ${u.person.lastName ?? ''}`.trim()) || u.username
            : u.username,
        }));
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load users' });
      } finally {
        this.loadingUsers = false;
      }
    },

    async loadCycles() {
      this.loadingCycles = true;
      try {
        const res   = await cycleSvc.fetchAll();
        const data  = res.data ?? res;
        this.cycles = (Array.isArray(data) ? data : []).filter(c => c.isActive);
      } catch { /* silent */ } finally {
        this.loadingCycles = false;
      }
    },

    async searchAccounts() {
      if (!this.accountSearch.query.trim()) {
        this.showSnack({ color: 'warning', title: 'Required', message: 'Enter a name or TRN' });
        return;
      }
      this.searchingAccounts      = true;
      this.accountSearch.results  = [];
      this.accountSearch.searched = false;
      try {
        const q       = this.accountSearch.query.trim();
        const filters = q.match(/^\d/) ? { trn: q } : { firstName: q };
        const res     = await memberSvc.searchMembers(filters, 0, 20);
        const data    = res.data ?? res;
        const persons = data.data ?? data;
        const results = [];
        for (const p of persons) {
          const accts = p.accounts ?? [];
          if (accts.length) {
            for (const pta of accts) {
              const a = pta.account ?? pta;
              results.push({ accountId: a.id, mainAccountNumber: a.mainAccountNumber, personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(), trn: p.trn ?? '' });
            }
          } else {
            results.push({ accountId: null, mainAccountNumber: '—', personName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim(), trn: p.trn ?? '' });
          }
        }
        this.accountSearch.results  = results;
        this.accountSearch.searched = true;
      } catch (e) {
        this.showSnack({ color: 'error', title: 'Error', message: 'Search failed' });
      } finally {
        this.searchingAccounts = false;
      }
    },

    async selectAccount(result) {
      if (!result.accountId) {
        this.showSnack({ color: 'warning', title: 'No account', message: 'This person has no linked account' });
        return;
      }
      this.newCase.selectedAccount = result;
      this.newCase.accountId       = result.accountId;
      this.accountSearch.results   = [];
      if (this.newCase.source === 'ALERT')   await this.loadOpenAlerts();
      if (this.newCase.source === 'ATTEMPT') await this.loadFlaggedAttempts();
    },

    clearSelectedAccount() {
      this.newCase.selectedAccount = null;
      this.newCase.accountId       = '';
      this.newCase.amlAlertId      = null;
      this.newCase.linkedAttemptId = null;
      this.openAlerts              = [];
      this.flaggedAttempts         = [];
      this.accountSearch.query     = '';
    },

    async loadOpenAlerts() {
      this.loadingAlerts = true;
      try {
        const res       = await svc.fetchOpenAlerts(this.newCase.selectedAccount.accountId);
        this.openAlerts = res.data ?? res;
      } catch { this.openAlerts = []; } finally { this.loadingAlerts = false; }
    },

    async loadFlaggedAttempts() {
      this.loadingAttempts = true;
      try {
        const res            = await svc.fetchFlaggedAttempts(this.newCase.selectedAccount.accountId);
        this.flaggedAttempts = res.data ?? res;
      } catch { this.flaggedAttempts = []; } finally { this.loadingAttempts = false; }
    },

    async submitNewCase() {
      if (!this.$refs.newCaseForm.validate()) return;
      if (!this.newCase.selectedAccount) {
        this.showSnack({ color: 'error', title: 'Required', message: 'Please select an account holder' });
        return;
      }
      this.saving = true;
      try {
        const payload = {
          accountId: this.newCase.selectedAccount.accountId, reportType: this.newCase.reportType,
          priority: this.newCase.priority, assignedToUserId: this.newCase.assignedToUserId, summary: this.newCase.summary,
        };
        if (this.newCase.source === 'ALERT'   && this.newCase.amlAlertId)      payload.amlAlertId      = this.newCase.amlAlertId;
        if (this.newCase.source === 'MANUAL'  && this.newCase.cycleId)         payload.cycleId         = this.newCase.cycleId;
        if (this.newCase.source === 'ATTEMPT' && this.newCase.linkedAttemptId) payload.linkedAttemptId = this.newCase.linkedAttemptId;
        const res    = await svc.create(payload);
        const result = res.data ?? res;
        if (this.newCase.source === 'ATTEMPT' && this.newCase.linkedAttemptId) {
          await svc.addTransaction(result.id, { attemptId: this.newCase.linkedAttemptId });
        }
        this.showSnack({ color: 'success', title: 'Case Opened', message: `Case ${result.caseReference} created` });
        this.closeNewCase();
        this.loadStats();
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to open case';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.saving = false;
      }
    },

    openReopenDialog(item) {
      this.reopenTarget       = item;
      this.reopenStep         = 1;
      this.reopenAssignUserId = null;
      this.reopenDialog       = true;
    },

    async confirmReopen() {
      this.reopening = true;
      try {
        await svc.reopen(this.reopenTarget.id);
        this.reopenStep = 2;
        this.loadStats();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to reopen case' });
      } finally {
        this.reopening = false;
      }
    },

    async reopenAssignToSelf() {
      try {
        await svc.assign(this.reopenTarget.id, this.loggedInUser.id);
        const target = { ...this.reopenTarget };
        this.closeReopenDialog();
        this.openCasePromptTarget = target;
        this.openCasePromptDialog = true;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to assign case' });
      }
    },

    async confirmReopenAssign() {
      if (!this.reopenAssignUserId) return;
      this.assigning = true;
      try {
        await svc.assign(this.reopenTarget.id, this.reopenAssignUserId);
        this.showSnack({ color: 'success', title: 'Assigned', message: `${this.reopenTarget.caseReference} assigned successfully` });
        this.closeReopenDialog();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to assign case' });
      } finally {
        this.assigning = false;
      }
    },

    closeReopenDialog() {
      this.reopenDialog       = false;
      this.reopenTarget       = null;
      this.reopenStep         = 1;
      this.reopenAssignUserId = null;
      this.setTableFilter('active');
      this.loadStats();
    },

    goToCaseFromPrompt() {
      this.openCasePromptDialog = false;
      if (this.openCasePromptTarget) {
        this.goToCase(this.openCasePromptTarget.id);
        this.openCasePromptTarget = null;
      }
    },

    goToCase(id) {
      this.$router.push({ name: 'aml-case-detail', params: { id } });
    },

    pipelinePct(count) { return Math.round((count / this.pipelineMax) * 100); },

    riskChipColor(score) {
      if (score >= 70) return 'red';
      if (score >= 40) return 'orange';
      return 'green';
    },
  },
};
</script>

<style scoped>
/* ── Layout ── */
.aml-dash { background: transparent; }

/* ── View tabs ── */
.aml-view-tabs { display:flex; gap:4px; border-bottom:0.5px solid #e0e0e0; padding-bottom:0; }
.aml-view-tab {
  font-size:13px; padding:9px 16px; border:none; background:transparent;
  color:#888; cursor:pointer; border-bottom:2px solid transparent;
  transition:all 0.15s; white-space:nowrap; display:inline-flex;
  align-items:center; gap:5px; font-family:inherit; position:relative; bottom:-0.5px;
}
.aml-view-tab:hover { color:#1a1a1a; }
.aml-view-tab--active { color:#185FA5; border-bottom-color:#185FA5; font-weight:500; }
.aml-view-tab-badge {
  font-size:10px; font-weight:700; padding:1px 6px;
  border-radius:10px; background:#FCEBEB; color:#A32D2D;
}

/* ── Typography ── */
.aml-overline {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #999;
}
.aml-title {
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.3px;
  margin: 0;
}

/* ── Buttons ── */
.aml-btn-ghost {
  font-size: 12px !important;
  border: 0.5px solid #ddd !important;
  background: transparent !important;
  color: inherit !important;
  box-shadow: none !important;
}
.aml-btn-primary {
  font-size: 12px !important;
  background: #185FA5 !important;
  color: #fff !important;
  box-shadow: none !important;
}
.aml-btn-analyse {
  font-size: 12px !important;
  background: #1D9E75 !important;
  color: #fff !important;
  box-shadow: none !important;
}

/* ── Metric cards ── */
.aml-metric-card {
  border: 0.5px solid #e0e0e0;
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: white;
  height: 100%;
}
.aml-metric-card:hover { background: #f7f9fc; }
.aml-metric-active { border-width: 1.5px !important; }
.aml-metric-blue   { border-color: #378ADD !important; background: #378ADD12 !important; }
.aml-metric-red    { border-color: #E24B4A !important; background: #E24B4A12 !important; }
.aml-metric-green  { border-color: #3B6D11 !important; background: #3B6D1112 !important; }
.aml-metric-purple { border-color: #534AB7 !important; background: #534AB712 !important; }
.aml-metric-label  { font-size: 11px; color: #777; margin-bottom: 6px; font-weight: 500; }
.aml-metric-value  { font-size: 28px; font-weight: 600; line-height: 1; margin-bottom: 6px; }
.aml-metric-sub    { font-size: 11px; color: #aaa; }

/* ── Cards ── */
.aml-card {
  border: 0.5px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  background: white;
}

/* ── Toolbar ── */
.aml-toolbar {
  padding: 12px 16px;
  border-bottom: 0.5px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Filter tabs ── */
.aml-filter-tab {
  font-size: 11px;
  padding: 4px 11px;
  border-radius: 20px;
  border: 0.5px solid #ddd;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 0.12s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.aml-filter-tab:hover { background: #f5f5f5; }
.aml-filter-tab--active { font-weight: 600; }
.aml-filter-count {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 10px;
  background: #f5f5f5;
  color: #aaa;
}

/* ── Search ── */
.aml-search-wrap { position: relative; }
.aml-search-icon { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: #bbb; }
.aml-search-input {
  font-size: 12px;
  padding: 5px 10px 5px 26px;
  border: 0.5px solid #ddd;
  border-radius: 8px;
  background: white;
  color: inherit;
  width: 160px;
  outline: none;
  font-family: inherit;
}
.aml-search-input:focus { border-color: #999; }

/* ── Table ── */
.aml-table-wrap { overflow-x: auto; }
.aml-table { width: 100%; border-collapse: collapse; font-size: 12px; table-layout: fixed; }
.aml-table thead tr { border-bottom: 0.5px solid #e0e0e0; }
.aml-table th {
  padding: 9px 14px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  color: #aaa;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}
.aml-table-row { border-bottom: 0.5px solid #e0e0e0; cursor: pointer; transition: background 0.1s; }
.aml-table-row:hover { background: #EBF3FC; }
.aml-table td { padding: 10px 14px; }
.aml-empty-row { padding: 32px 20px; text-align: center; color: #aaa; font-size: 13px; }
.aml-table-footer {
  padding: 9px 16px;
  border-top: 0.5px solid #e0e0e0;
  font-size: 11px;
  color: #aaa;
}

/* ── Table cell helpers ── */
.aml-ref-link    { color: #185FA5; font-weight: 600; }
.aml-muted       { color: #777; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aml-truncate    { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100px; }
.aml-unassigned  { color: #bbb; font-size: 11px; }
.aml-type-badge  {
  font-size: 10px;
  font-weight: 700;
  color: #666;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}
.aml-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}
.aml-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  font-family: monospace;
  letter-spacing: 0.5px;
}
.aml-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 0.5px solid #e0e0e0;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.1s;
}
.aml-icon-btn:hover { background: #f0f0f0; }
.aml-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── Right panel ── */
.aml-panel-header {
  padding: 11px 14px;
  border-bottom: 0.5px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
}
.aml-count-pill {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
}
.aml-count-purple { background: #EEEDFE; color: #534AB7; }
.aml-empty-panel  { padding: 16px; text-align: center; color: #bbb; font-size: 12px; }
.aml-my-case-row {
  padding: 9px 14px;
  border-bottom: 0.5px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.1s;
}
.aml-my-case-row:last-child { border-bottom: none; }
.aml-my-case-row:hover { background: #f7f9fc; }

/* ── Progress bars ── */
.aml-progress-track {
  height: 4px;
  border-radius: 4px;
  background: #eee;
  overflow: hidden;
}
.aml-progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

/* ── Misc ── */
.aml-divider { height: 0.5px; background: #e0e0e0; }

/* ── Analysis overlay ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.aml-analysis-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(3px);
}
.aml-analysis-overlay__card {
  background: #fff;
  border-radius: 12px;
  padding: 40px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.18);
  max-width: 380px;
  text-align: center;
}
.aml-analysis-overlay__title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
}
.aml-analysis-overlay__sub {
  font-size: 12px;
  color: #777;
  line-height: 1.6;
}

/* ── Pagination ── */
.aml-page-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0.5px solid #ddd;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.1s;
}
.aml-page-btn:hover:not(:disabled) { background: #f0f0f0; }
.aml-page-btn:disabled { opacity: 0.35; cursor: not-allowed; }
</style>